import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  getListing, saveListing, unsaveListing, deleteListing, recordContact,
} from '../services/marketplace';
import {
  money, CONDITION_LABELS, TIER_LABELS, ROAD_LEGALITY_LABELS, SOLD_AS_LABELS,
  WHEELS_DEPARTMENT_SLUG,
} from '../constants/marketplace';

// ── Icons (inline SVG, Lucide / Feather style) ───────────────────────────────
const Icon = {
  Back: (props) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  Bookmark: ({ filled, ...props }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
    </svg>
  ),
  Message: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
  Pencil: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  Trash: (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  ),
  Car: (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M5 17H3v-5l2-5h14l2 5v5h-2" /><circle cx="7.5" cy="17" r="1.5" /><circle cx="16.5" cy="17" r="1.5" />
    </svg>
  ),
  Globe: (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  ),
  Image: (props) => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  Alert: (props) => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtFitment = (f) => {
  const years = f.yearStart && f.yearEnd
    ? (f.yearStart === f.yearEnd ? `${f.yearStart}` : `${f.yearStart}–${f.yearEnd}`)
    : (f.yearStart ? `${f.yearStart}+` : (f.yearEnd ? `up to ${f.yearEnd}` : ''));
  return [years, f.make, f.model, f.badge].filter(Boolean).join(' ')
    + (f.engineCode ? `  ·  ${f.engineCode}` : '')
    + (f.position ? `  ·  ${f.position}` : '');
};

const SpecRow = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="mkt-spec-row">
      <span className="mkt-spec-label">{label}</span>
      <span className="mkt-spec-value">{value}</span>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mkt-section">
    <h3 className="mkt-section-title">{title}</h3>
    {children}
  </div>
);

const Badge = ({ text, warn }) => {
  if (!text) return null;
  return <span className={`mkt-badge${warn ? ' warn' : ''}`}>{text}</span>;
};

// ── Page ─────────────────────────────────────────────────────────────────────
const MarketplaceListing = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [contacting, setContacting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await getListing(id);
      setListing(data.listing);
      setSaved(!!data.listing.savedBy?.some((x) => x.userId === user?.id));
      setImgIndex(0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => { load(); }, [load]);

  const toggleSave = async () => {
    if (!user) { toast.info('Please sign in to save listings.'); return; }
    const next = !saved;
    setSaved(next);
    setListing((prev) => prev && ({ ...prev, savedCount: Math.max(0, (prev.savedCount || 0) + (next ? 1 : -1)) }));
    try {
      if (next) await saveListing(id); else await unsaveListing(id);
    } catch {
      setSaved(!next);
      setListing((prev) => prev && ({ ...prev, savedCount: Math.max(0, (prev.savedCount || 0) + (next ? -1 : 1)) }));
      toast.error('Could not update saved listings.');
    }
  };

  const contactSeller = async () => {
    if (!user) { toast.info('Please sign in to message the seller.'); return; }
    if (!listing?.sellerId) { toast.error('Seller info is missing for this listing.'); return; }
    setContacting(true);
    try { await recordContact(id); } catch { /* non-blocking */ }
    try {
      // Start (or fetch) the DM with the seller, then go to Messages.
      // Messages.jsx loads conversations from the server on mount, so the
      // newly-created DM appears in the list without needing to pass state.
      await api.post('/messages/conversations/dm', { userId: listing.sellerId });
      navigate('/messages');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start a conversation.');
    } finally {
      setContacting(false);
    }
  };

  const handleDelete = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this listing? This will delist the item.')) return;
    try {
      await deleteListing(id);
      toast.success('Listing removed.');
      navigate('/marketplace/saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove listing.');
    }
  };

  if (loading) {
    return (
      <div className="mkt-page">
        <div className="mkt-topbar">
          <button className="mkt-back-btn" onClick={() => navigate(-1)} aria-label="Back"><Icon.Back /></button>
        </div>
        <div className="mkt-state"><div className="mkt-spinner" /></div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="mkt-page">
        <div className="mkt-topbar">
          <button className="mkt-back-btn" onClick={() => navigate(-1)} aria-label="Back"><Icon.Back /></button>
        </div>
        <div className="mkt-state">
          <Icon.Alert />
          <span className="mkt-state-title">Listing not available</span>
        </div>
      </div>
    );
  }

  const images = listing.images || [];
  const isOwner = !!user && user.id === listing.sellerId;
  const isWheel = listing.category?.departmentSlug === WHEELS_DEPARTMENT_SLUG;
  const delivery = [
    listing.deliveryPickup && 'Pickup',
    listing.deliveryShipping && 'Shipping',
  ].filter(Boolean).join(' · ');
  const locationStr = [listing.suburb, listing.region, listing.country].filter(Boolean).join(', ');
  const activeImg = images[imgIndex] || images[0];
  const mainSrc = activeImg?.imageUrl || activeImg?.thumbnailUrl || listing.primaryImageUrl;

  return (
    <div className="mkt-page">
      <div className="mkt-topbar">
        <button className="mkt-back-btn" onClick={() => navigate(-1)} aria-label="Back"><Icon.Back /></button>
      </div>

      <div className="mkt-detail-grid">
        {/* ── Gallery ── */}
        <div className="mkt-gallery">
          <div className="mkt-gallery-main">
            {mainSrc ? (
              <img src={mainSrc} alt={listing.title} />
            ) : (
              <div className="mkt-gallery-empty"><Icon.Image /></div>
            )}
            {listing.status === 'sold' ? <span className="mkt-sold-tag">SOLD</span> : null}
          </div>
          {images.length > 1 ? (
            <div className="mkt-thumbs">
              {images.map((im, i) => (
                <button
                  key={im.id}
                  type="button"
                  className={`mkt-thumb${i === imgIndex ? ' active' : ''}`}
                  onClick={() => setImgIndex(i)}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={im.thumbnailUrl || im.imageUrl} alt="" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* ── Body ── */}
        <div className="mkt-detail-body">
          <p className="mkt-price">
            {money(listing.price, listing.currency)}
            <span className="mkt-currency">{listing.currency}</span>
          </p>
          {listing.isNegotiable ? <p className="mkt-negotiable">Negotiable</p> : null}
          <h1 className="mkt-title">{listing.title}</h1>

          <div className="mkt-badge-row">
            <Badge text={CONDITION_LABELS[listing.condition] || listing.condition} />
            {listing.partTier ? <Badge text={TIER_LABELS[listing.partTier]} /> : null}
            {listing.usedGrade ? <Badge text={`Grade ${listing.usedGrade}`} /> : null}
            {listing.roadLegality && listing.roadLegality !== 'street'
              ? <Badge text={ROAD_LEGALITY_LABELS[listing.roadLegality]} warn /> : null}
          </div>

          {/* Fitment */}
          <Section title="Fits">
            {listing.isUniversal ? (
              <div className="mkt-fit-row">
                <Icon.Globe />
                <span>Fits all vehicles (universal)</span>
              </div>
            ) : listing.fitments?.length ? (
              listing.fitments.map((f) => (
                <div key={f.id} className="mkt-fit-row">
                  <Icon.Car />
                  <span>{fmtFitment(f)}</span>
                </div>
              ))
            ) : (
              <p className="mkt-muted">Fitment not specified — message the seller to confirm.</p>
            )}
          </Section>

          {/* Wheel specs — only for the wheels department */}
          {isWheel ? (
            <Section title="Wheel specifications">
              <SpecRow label="Bolt pattern" value={listing.wheelBoltPattern} />
              <SpecRow label="PCD" value={listing.wheelPcdMm != null && listing.wheelPcdMm !== '' ? `${Number(listing.wheelPcdMm)} mm` : null} />
              <SpecRow label="Diameter" value={listing.wheelDiameterIn ? `${Number(listing.wheelDiameterIn)}"` : null} />
              <SpecRow label="Width" value={listing.wheelWidthIn ? `${Number(listing.wheelWidthIn)}"` : null} />
              <SpecRow label="Offset (ET)" value={listing.wheelOffsetMm != null && listing.wheelOffsetMm !== '' ? `${Number(listing.wheelOffsetMm)} mm` : null} />
              <SpecRow label="Centre bore" value={listing.wheelCenterBoreMm ? `${Number(listing.wheelCenterBoreMm)} mm` : null} />
              {listing.isStaggered ? (
                <SpecRow label="Rear" value={[
                  listing.wheelRearDiameterIn ? `${Number(listing.wheelRearDiameterIn)}"` : null,
                  listing.wheelRearWidthIn ? `${Number(listing.wheelRearWidthIn)}"` : null,
                  listing.wheelRearOffsetMm != null && listing.wheelRearOffsetMm !== '' ? `ET${Number(listing.wheelRearOffsetMm)}` : null,
                ].filter(Boolean).join(' / ')} />
              ) : null}
            </Section>
          ) : null}

          {/* Details */}
          <Section title="Details">
            <SpecRow label="Brand" value={listing.brand} />
            <SpecRow label="Part number" value={listing.partNumber} />
            {listing.soldAs ? <SpecRow label="Sold as" value={SOLD_AS_LABELS[listing.soldAs] || listing.soldAs} /> : null}
            {listing.quantity != null ? <SpecRow label="Quantity" value={listing.quantity} /> : null}
            {listing.coreRequired ? (
              <SpecRow label="Core charge" value={listing.coreDeposit ? `${money(listing.coreDeposit, listing.currency)} (refundable)` : 'Required'} />
            ) : null}
            <SpecRow label="Delivery" value={delivery || '—'} />
            {listing.deliveryShipping && listing.shippingCost ? (
              <SpecRow label="Shipping" value={money(listing.shippingCost, listing.currency)} />
            ) : null}
            <SpecRow label="Location" value={locationStr} />
          </Section>

          {/* Description */}
          {listing.description ? (
            <Section title="Description">
              <p className="mkt-description">{listing.description}</p>
            </Section>
          ) : null}

          {/* Seller */}
          <Section title="Seller">
            {listing.seller?.username ? (
              <Link to={`/profile/${listing.seller.username}`} className="mkt-seller">
                {listing.seller.profileImageUrl ? (
                  <img className="mkt-seller-avatar" src={listing.seller.profileImageUrl} alt="" />
                ) : (
                  <span className="mkt-seller-avatar mkt-seller-avatar-fallback">
                    {(listing.seller.username[0] || '?').toUpperCase()}
                  </span>
                )}
                <span>
                  <span className="mkt-seller-name">{listing.seller.username}</span>
                  <span className="mkt-seller-meta" style={{ display: 'block' }}>
                    {listing.viewCount || 0} views · {listing.savedCount || 0} saved
                  </span>
                </span>
              </Link>
            ) : (
              <p className="mkt-muted">Seller unavailable</p>
            )}
          </Section>

          {/* Actions */}
          <div className="mkt-actions">
            {isOwner ? (
              <>
                <button
                  type="button"
                  className="mkt-btn mkt-btn-primary"
                  onClick={() => navigate(`/marketplace/edit/${listing.id}`)}
                >
                  <Icon.Pencil /> Edit
                </button>
                <button
                  type="button"
                  className="mkt-btn mkt-btn-danger"
                  onClick={handleDelete}
                  aria-label="Delete listing"
                >
                  <Icon.Trash />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="mkt-btn mkt-btn-primary"
                  onClick={contactSeller}
                  disabled={contacting}
                >
                  <Icon.Message /> {contacting ? 'Starting…' : 'Contact seller'}
                </button>
                <button
                  type="button"
                  className={`mkt-btn mkt-btn-outline${saved ? ' active' : ''}`}
                  onClick={toggleSave}
                >
                  <Icon.Bookmark filled={saved} /> {saved ? 'Saved' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceListing;
